import type {
  CargaTrabalhoObra,
  CargaTrabalhoObrasData,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissionaisData,
  GanttIndicators,
} from "@/modules/carga-trabalho";
import type { CargaFornecedorDados } from "./carga-fornecedor.filters";
import type { FornecedorAlocacoes, ObraResumo } from "./recursos.types";

/**
 * Adaptador da Carga de Trabalho de Fornecedores (US-1774) para o Gantt de
 * Profissionais: converte as alocações (já filtradas) no modelo de entrada dele.
 * A API não traz etapas (planejamento/obra/fechamento): a barra é a própria alocação.
 */

export interface CargaFornecedorIndicadores {
  obra: boolean;
  cancelada: boolean;
}

export function toGanttObra(obra: ObraResumo): CargaTrabalhoObra {
  return {
    id: obra.id,
    codigo: obra.codigo,
    nome: obra.nome,
    situacao: obra.situacao,
    embarcacao: obra.embarcacao ?? null,
    etapas: { planejamento: null, obra: null, fechamento: null },
  };
}

/** Fornecedor faz o papel de "profissional": uma linha por fornecedor. */
export function toGanttProfissionaisData(
  dados: Extract<CargaFornecedorDados, { agrupamento: "fornecedor" }>,
  periodo: CargaTrabalhoPeriodo,
): CargaTrabalhoProfissionaisData {
  return {
    periodo,
    profissionais: dados.fornecedores.map((fornecedor) => ({
      id: fornecedor.id || fornecedor.nome,
      nome: fornecedor.nome,
      time: null,
      indisponibilidades: [],
      conflitosIgnorados: (fornecedor.conflitosIgnorados ?? []).map(({ alocacaoIds }) => ({
        alocacaoIds,
      })),
      alocacoes: fornecedor.alocacoes.map((alocacao) => ({
        id: alocacao.id,
        papelObraId: null,
        inicio: alocacao.inicio,
        fim: alocacao.fim,
        obra: toGanttObra(alocacao.obra),
      })),
    })),
  };
}

/**
 * Visão por Obras reagrupada por fornecedor: base de "Resolver Conflito", que trata o conflito do
 * fornecedor (todas as suas obras) e precisa das alocações dos candidatos a substituto.
 */
export function obrasParaProfissionais(
  dados: Extract<CargaFornecedorDados, { agrupamento: "obra" }>,
  periodo: CargaTrabalhoPeriodo,
): CargaTrabalhoProfissionaisData {
  const porFornecedor = new Map<string, FornecedorAlocacoes>();
  for (const { alocacoes, ...obra } of dados.obras) {
    for (const alocacao of alocacoes) {
      const { fornecedor } = alocacao;
      const key = fornecedor.id || fornecedor.nome;
      const atual = porFornecedor.get(key) ?? { ...fornecedor, alocacoes: [] };
      atual.alocacoes.push({ id: alocacao.id, inicio: alocacao.inicio, fim: alocacao.fim, obra });
      porFornecedor.set(key, atual);
    }
  }
  return toGanttProfissionaisData(
    { agrupamento: "fornecedor", fornecedores: [...porFornecedor.values()] } as Extract<
      CargaFornecedorDados,
      { agrupamento: "fornecedor" }
    >,
    periodo,
  );
}

/** Fornecedor faz o papel de "profissional" alocado na obra. */
export function toGanttObrasData(
  dados: Extract<CargaFornecedorDados, { agrupamento: "obra" }>,
  periodo: CargaTrabalhoPeriodo,
): CargaTrabalhoObrasData {
  return {
    periodo,
    obras: dados.obras.map((obra) => ({
      ...toGanttObra(obra),
      profissionais: obra.alocacoes.map((alocacao) => ({
        id: alocacao.fornecedor.id || alocacao.fornecedor.nome,
        nome: alocacao.fornecedor.nome,
        time: null,
        papelObraId: null,
        inicio: alocacao.inicio,
        fim: alocacao.fim,
      })),
    })),
  };
}

/** Indicadores do Gantt de Profissionais: só Obra e Cancelada existem para fornecedores. */
export function toGanttIndicators(indicadores: CargaFornecedorIndicadores): GanttIndicators {
  return {
    planejamento: true,
    obra: indicadores.obra,
    fechamento: true,
    cancelada: indicadores.cancelada,
    indisponibilidade: false,
  };
}

export function countCargaFornecedorLinhas(dados: CargaFornecedorDados | null): number {
  if (!dados) return 0;
  return dados.agrupamento === "obra" ? dados.obras.length : dados.fornecedores.length;
}
